import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyCompanyPage: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    // 履歴があるかチェックして、なければチャットページに戻る
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/chat');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            戻る
          </Button>
        </Box>

        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Title */}
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 4,
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            企業向け個人情報保護方針
          </Typography>

          {/* Company Information */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              会社名：株式会社Gerbera
            </Typography>
            <Typography variant="body1" color="text.secondary">
              代表取締役：𠮷岡 歩真
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Introduction */}
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            株式会社Gerbera（以下「弊社」）は、企業ユーザーの個人情報の保護に関して、個人情報保護法をはじめとする関連法令を遵守し、適切な取扱いを行うことを宣言いたします。本プライバシーポリシーは、弊社が提供するサービス「ガクフドキャリア」において、企業ユーザーの個人情報をどのように収集、利用、管理するかについて定めるものです。
          </Typography>

          {/* Privacy Policy Items */}
          <List sx={{ mb: 4 }}>
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    1. 個人情報の収集について
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      弊社は、以下の個人情報を収集いたします：
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 企業情報：企業名、所在地、電話番号、FAX番号、メールアドレス
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 担当者情報：氏名、部署名、役職、連絡先
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 求人情報：職種、勤務地、給与、勤務時間、福利厚生等
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • サービス利用情報：ログイン履歴、アクセス記録、利用状況
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      • その他：お問い合わせ内容、アンケート回答等
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    2. 個人情報の利用目的
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      弊社は、収集した個人情報を以下の目的で利用いたします：
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 人材紹介サービスの提供・運営
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 求人情報の掲載・管理
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 候補者とのマッチング・紹介
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • サービスに関する連絡・通知
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 利用料金の請求・支払い処理
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • サービス改善・新サービス開発
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 統計データの作成（個人を特定できない形式）
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      • 法令に基づく対応・報告
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    3. 個人情報の第三者提供
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      弊社は、以下の場合を除き、個人情報を第三者に提供いたしません：
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 企業ユーザーの同意がある場合
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 法令に基づく場合
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 人の生命、身体または財産の保護のために必要がある場合
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      • 業務委託先への提供（利用目的の達成に必要な範囲内で、適切な監督を行います）
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      なお、統計データとして個人を特定できない形式で提供する場合は、第三者提供に該当しません。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    4. 個人情報の安全管理措置
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      弊社は、個人情報の漏洩、滅失、毀損の防止その他個人情報の安全管理のために、以下の措置を講じます：
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 物理的安全管理措置：サーバー室への入退室管理、機器の盗難防止等
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 技術的安全管理措置：アクセス制御、暗号化、ファイアウォール等
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 人的安全管理措置：従業員への教育・研修、機密保持契約の締結等
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 組織的安全管理措置：個人情報保護規程の策定、責任者の設置等
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      • 定期的なセキュリティ監査・見直しの実施
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    5. 個人情報の開示・訂正・削除等
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      企業ユーザーは、弊社が保有する自己の個人情報について、以下の請求を行うことができます：
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 個人情報の開示請求
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 個人情報の訂正・追加・削除請求
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 個人情報の利用停止・消去請求
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      • 個人情報の第三者提供の停止請求
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      これらの請求については、本人確認を行った上で、合理的な期間内に対応いたします。なお、法令により開示等を拒否できる場合があります。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    6. Cookie等の使用について
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                      弊社のウェブサイトでは、以下の目的でCookie等を使用する場合があります：
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • ユーザーの利便性向上
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • サービス利用状況の分析
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, pl: 2 }}>
                      • 広告配信の最適化
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      Cookieの使用を希望されない場合は、ブラウザの設定により無効にすることができます。ただし、一部の機能が利用できなくなる場合があります。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    7. プライバシーポリシーの変更
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.7 }}>
                    弊社は、法令の改正やサービス内容の変更等に伴い、本プライバシーポリシーを変更する場合があります。変更する場合は、ウェブサイト上で事前に通知いたします。重要な変更については、メール等で個別に通知いたします。
                  </Typography>
                }
              />
            </ListItem>

            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    8. 個人情報保護に関するお問い合わせ先
                  </Typography>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      電話番号：06-7176-5480
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      FAX番号：06-7635-8254
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      E-mail：info@gakufudo-career.jp
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      受付時間：平日 9:00～18:00（土日祝日を除く）
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </List>

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              ２０２５年9月9日制定
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Footer */}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              以上
            </Typography>
          </Box>
        </Paper>

        {/* Bottom Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography 
            variant="body2" 
            color="white" 
            sx={{ opacity: 0.8 }}
          >
            © {new Date().getFullYear()} 株式会社Gerbera. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyCompanyPage;
